import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./organizerApi";

// Shared Courts ledger — layouts, groups, courts, and the live claim on each court.
// Backed by the `sharedcourts` Cloud Function in scoreholio-prod
// (src/functions/sharedcourts.js). Every route is organizer-secured, so this reuses
// organizerApi's baseQueryWithReauth rather than re-implementing token handling.
//
// Reads are mutations, matching the rest of this app — the backend takes POST bodies.
//
// NOTE: the live court grid does NOT go through here. It reads Firestore directly
// via onSnapshot, the same way the dashboard does, because allocation changes need
// to land on screen without a refetch. This API is for setup and control only.

export const sharedCourtsApi = createApi({
  reducerPath: "sharedCourtsApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Everything for one layout: its groups, its courts, and the live claim on each.
    // Defaults to the active layout when layoutId is omitted.
    getSharedCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/get`,
        method: "POST",
        body: body,
      }),
    }),
    // Create or resize a layout. Growing adds courts; shrinking disables the extras
    // rather than deleting them, since a court doc can carry history.
    saveLayout: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/layout/save`,
        method: "POST",
        body: body,
      }),
    }),
    // Name, fill strategy, terminology, on/off, tournament membership.
    // A group with one tournament is a dedicated assignment — same doc, same path.
    saveGroup: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/group/save`,
        method: "POST",
        body: body,
      }),
    }),
    // Which courts belong to a group. A court belongs to exactly one, so listing it
    // here removes it from whatever group it was in. Empty groupId pulls it out of all.
    setGroupCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/group/courts`,
        method: "POST",
        body: body,
      }),
    }),
    // Kick the match off a court: players back to check-in, claim released.
    freeCourt: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/court/free`,
        method: "POST",
        body: body,
      }),
    }),
    // Delete disabled, unclaimed courts. Anything else in the list is skipped
    // with a reason.
    deleteCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/court/delete`,
        method: "POST",
        body: body,
      }),
    }),
    // Rename / pause / disable, one court or many.
    saveCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/court/save`,
        method: "POST",
        body: body,
      }),
    }),
    // Tournament list for the group picker — this account's recent + upcoming
    // games across all sports, since groups span sports.
    getGroupableTournaments: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/tournaments`,
        method: "POST",
        body: body,
      }),
    }),
    // Manual broker wake — the Advance button. Safe to spam; a running drain just
    // picks the work up.
    advanceCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/advance`,
        method: "POST",
        body: body,
      }),
    }),
    // The broker's assignment log, newest first.
    getDecisions: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/decisions/get`,
        method: "POST",
        body: body,
      }),
    }),
    // Which courts a tournament holds right now. However many come back is how many
    // there are — nothing here caps the count.
    getTournamentCourts: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/tournament/courts`,
        method: "POST",
        body: body,
      }),
    }),
    // The account's remote-mode devices — the same devicecontrol docs the app
    // manages, so a device works in both worlds.
    getDevices: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/devices/get`,
        method: "POST",
        body: body,
      }),
    }),
    // Point a device at a shared court's scoreboard (court > 0) or send it back
    // to the device-login screen (court 0). The device follows the loadurl change.
    assignDevice: builder.mutation({
      query: (body) => ({
        url: `sharedcourts/devices/assign`,
        method: "POST",
        body: body,
      }),
    }),
  }),
});

export const {
  useGetSharedCourtsMutation,
  useSaveLayoutMutation,
  useSaveGroupMutation,
  useSetGroupCourtsMutation,
  useSaveCourtsMutation,
  useDeleteCourtsMutation,
  useFreeCourtMutation,
  useGetGroupableTournamentsMutation,
  useAdvanceCourtsMutation,
  useGetDecisionsMutation,
  useGetTournamentCourtsMutation,
  useGetDevicesMutation,
  useAssignDeviceMutation,
} = sharedCourtsApi;
